import React, { useState } from 'react';
import { Form, Row, Col, InputGroup, Alert, Table, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ContributionDetails = ({ contribution, onChange, salary, netMonthlyIncome }) => {
  const [showMatchTable, setShowMatchTable] = useState(false);

  const handleChange = (field, value) => {
    onChange({ ...contribution, [field]: value });
  };

  // Derive disposable income directly - no state writeback needed
  const calculatedDisposable = netMonthlyIncome
      ? Math.max(0, netMonthlyIncome - (contribution.monthlyEssentials || 0) - (contribution.monthlyNonEssentials || 0))
      : null;

  // Handle employer match table changes
  const handleMatchTableChange = (employeePercent, employerPercent) => {
    const newMatchTable = { ...(contribution.employerMatchTable || {}) };
    newMatchTable[String(employeePercent)] = employerPercent;

    // Batch all updates in a single onChange call
    const updates = { ...contribution, employerMatchTable: newMatchTable };

    // Always sync employer contribution from the updated table
    const currentEmployeeContrib = Math.floor(contribution.employeeContribution || 0);
    let employerFromTable = newMatchTable[String(currentEmployeeContrib)];

    // If employee % exceeds table range, use the max row
    if (employerFromTable === undefined) {
      const tableKeys = Object.keys(newMatchTable).map(Number).filter(n => !isNaN(n));
      if (tableKeys.length > 0) {
        const maxKey = Math.max(...tableKeys);
        if (currentEmployeeContrib > maxKey) {
          employerFromTable = newMatchTable[String(maxKey)];
        }
      }
    }

    if (employerFromTable !== undefined) {
      updates.employerContribution = employerFromTable;
    }

    onChange(updates);
  };

  // Clamp match table value on blur (when user leaves the field)
  const handleMatchTableBlur = (employeePercent) => {
    const table = contribution.employerMatchTable || {};
    const currentVal = table[String(employeePercent)] || 0;
    if (currentVal > 100) {
      handleMatchTableChange(employeePercent, 100);
    } else if (currentVal < 0) {
      handleMatchTableChange(employeePercent, 0);
    }
  };

  // Calculate employer contribution from match table
  const getEmployerContributionFromTable = (employeePercent) => {
    const table = contribution.employerMatchTable || {};
    const floored = Math.floor(employeePercent);
    const key = String(floored);

    // Direct lookup first
    if (table[key] !== undefined) {
      return table[key];
    }

    // If employee % exceeds the table's max row, use the max row's value
    // e.g. table goes 0-10%, employee is 11% -> use 10% row
    const tableKeys = Object.keys(table).map(Number).filter(n => !isNaN(n));
    if (tableKeys.length > 0) {
      const maxKey = Math.max(...tableKeys);
      if (floored > maxKey) {
        return table[String(maxKey)] || 0;
      }
    }

    return 0;
  };

  // Calculate monthly amounts for display
  const monthlyEmployeeContribution = salary && contribution.employeeContribution
      ? Math.round((salary * (contribution.employeeContribution / 100)) / 12)
      : 0;

  const monthlyEmployerContribution = salary && contribution.employerContribution
      ? Math.round((salary * (contribution.employerContribution / 100)) / 12)
      : 0;

  const totalMonthlyContribution = monthlyEmployeeContribution + monthlyEmployerContribution;

  // Find optimal contribution (max employer match)
  const findOptimalContribution = () => {
    const table = contribution.employerMatchTable || {};
    let maxEmployerContrib = 0;
    let optimalEmployeeContrib = 0;

    for (let i = 0; i <= 10; i++) {
      const employerContrib = table[i] || 0;
      if (employerContrib > maxEmployerContrib) {
        maxEmployerContrib = employerContrib;
        optimalEmployeeContrib = i;
      }
    }
    return { optimalEmployeeContrib, maxEmployerContrib };
  };

  const { optimalEmployeeContrib, maxEmployerContrib } = findOptimalContribution();
  const missingEmployerMatch = optimalEmployeeContrib > 0 &&
      contribution.employeeContribution < optimalEmployeeContrib;

  return (
      <Row>
        {/* Employer Match Structure - NEW TABLE */}
        <Col md={12}>
          <div className="profile-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <div className="card-icon warning me-3">
                  <FontAwesomeIcon icon="handshake" />
                </div>
                <div>
                  <label className="mb-0 fw-bold">Employer Contribution Structure</label>
                  <div className="text-muted small">How much does your employer contribute based on your contribution?</div>
                </div>
              </div>
              <Button
                  variant={showMatchTable ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setShowMatchTable(!showMatchTable)}
              >
                <FontAwesomeIcon icon={showMatchTable ? "chevron-up" : "chevron-down"} className="me-1" />
                {showMatchTable ? "Hide Table" : "Set Up Matching"}
              </Button>
            </div>

            {showMatchTable && (
                <div className="p-3 bg-light rounded mb-3">
                  <p className="small text-muted mb-3">
                    Enter what your employer contributes for each level of your contribution.
                    Leave at 0 if no contribution at that level.
                  </p>
                  <Table size="sm" bordered className="mb-0 bg-white">
                    <thead className="table-light">
                    <tr>
                      <th className="text-center" style={{width: '50%'}}>If I contribute</th>
                      <th className="text-center" style={{width: '50%'}}>My employer contributes</th>
                    </tr>
                    </thead>
                    <tbody>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((percent) => {
                      const employeePct = Math.floor(contribution.employeeContribution || 0);
                      const isActiveRow = employeePct === percent ||
                          (employeePct > 10 && percent === 10); // Cap highlight at max row
                      return (
                          <tr key={percent} style={isActiveRow ? { backgroundColor: '#e8f5e9' } : {}}>
                            <td className="text-center align-middle">
                              {percent}%
                              {isActiveRow && <span className="ms-1 text-success small">◄ current</span>}
                            </td>
                            <td className="p-1">
                              <InputGroup size="sm">
                                <Form.Control
                                    type="number"
                                    value={contribution.employerMatchTable?.[String(percent)] ?? ''}
                                    onChange={(e) => handleMatchTableChange(percent, e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    onBlur={() => handleMatchTableBlur(percent)}
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    placeholder="0"
                                    className="text-center"
                                    style={isActiveRow ? { backgroundColor: '#f0fff4', fontWeight: 600 } : {}}
                                />
                                <InputGroup.Text>%</InputGroup.Text>
                              </InputGroup>
                            </td>
                          </tr>
                      );
                    })}
                    </tbody>
                  </Table>
                  <div className="mt-2 small text-muted">
                    <FontAwesomeIcon icon="lightbulb" className="me-1 text-warning" />
                    Examples: "1:1 match up to 5%" = enter 5% employer for 5% employee.
                    "Double match" = enter 10% employer for 5% employee.
                  </div>
                </div>
            )}

            {/* Quick summary of match structure */}
            {!showMatchTable && optimalEmployeeContrib > 0 && (
                <Alert variant="light" className="mb-0 border">
                  <FontAwesomeIcon icon="check-circle" className="me-2 text-success" />
                  Match structure set: Employer contributes up to <strong>{maxEmployerContrib}%</strong> when you contribute <strong>{optimalEmployeeContrib}%</strong>
                </Alert>
            )}
          </div>
        </Col>

        {/* Current Employee Contribution */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="percentage" />
            </div>
            <Form.Group className="mb-3">
              <label>What is your current pension contribution?</label>
              <InputGroup>
                <Form.Control
                    type="number"
                    value={contribution.employeeContribution ?? ''}
                    onChange={(e) => {
                      const newValue = e.target.value === '' ? '' : parseFloat(e.target.value);
                      // Batch both updates in a single onChange to avoid state collision
                      const employerFromTable = getEmployerContributionFromTable(newValue || 0);
                      const updates = { ...contribution, employeeContribution: newValue };
                      // Always update employer from table (even if 0 means no match)
                      if (employerFromTable >= 0) {
                        updates.employerContribution = employerFromTable;
                      }
                      onChange(updates);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        const employerFromTable = getEmployerContributionFromTable(0);
                        const updates = { ...contribution, employeeContribution: 0 };
                        if (employerFromTable >= 0) updates.employerContribution = employerFromTable;
                        onChange(updates);
                      }
                    }}
                    min={0}
                    max={100}
                    step={0.5}
                />
                <InputGroup.Text>% of salary</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                {monthlyEmployeeContribution > 0 && `£${monthlyEmployeeContribution.toLocaleString()}/month`}
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Employer Contribution (auto-calculated from table if available) */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="building" />
            </div>
            <Form.Group className="mb-3">
              <label>Your employer currently contributes</label>
              <InputGroup>
                <Form.Control
                    type="number"
                    value={contribution.employerContribution ?? ''}
                    onChange={(e) => handleChange('employerContribution', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('employerContribution', 0); }}
                    min={0}
                    max={100}
                    step={0.5}
                    style={getEmployerContributionFromTable(contribution.employeeContribution || 0) > 0
                        ? { backgroundColor: '#f0fff4', fontWeight: 600 }
                        : {}}
                />
                <InputGroup.Text>% of salary</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                {getEmployerContributionFromTable(contribution.employeeContribution || 0) > 0
                    ? <>Based on your {contribution.employeeContribution}% contribution (from match table above). {monthlyEmployerContribution > 0 && `£${monthlyEmployerContribution.toLocaleString()}/month`}</>
                    : <>{monthlyEmployerContribution > 0 ? `£${monthlyEmployerContribution.toLocaleString()}/month` : 'Set the match table above, or enter manually'}</>
                }
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Salary Sacrifice */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="exchange-alt" />
            </div>
            <Form.Group className="mb-3">
              <label>Is salary sacrifice available for pension contributions?</label>
              <Form.Select
                  value={contribution.salarySacrificeAvailable}
                  onChange={(e) => handleChange('salarySacrificeAvailable', e.target.value)}
              >
                <option value="yes">Yes — salary sacrifice is available</option>
                <option value="no">No — contributions are from net pay</option>
                <option value="unsure">I'm not sure</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Salary sacrifice can save National Insurance
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Monthly Essential Expenses */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon danger">
              <FontAwesomeIcon icon="home" />
            </div>
            <Form.Group className="mb-3">
              <label>What are your essential monthly expenses?</label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={contribution.monthlyEssentials ?? ''}
                    onChange={(e) => handleChange('monthlyEssentials', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('monthlyEssentials', 0); }}
                    min={0}
                    step={50}
                />
                <InputGroup.Text>/month</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Mortgage/rent, utilities, insurance, food, transport — things you must pay
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Monthly Non-Essential Expenses */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon warning">
              <FontAwesomeIcon icon="shopping-bag" />
            </div>
            <Form.Group className="mb-3">
              <label>What are your non-essential monthly expenses?</label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={contribution.monthlyNonEssentials ?? ''}
                    onChange={(e) => handleChange('monthlyNonEssentials', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('monthlyNonEssentials', 0); }}
                    min={0}
                    step={50}
                />
                <InputGroup.Text>/month</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Entertainment, dining out, subscriptions, holidays — flexible spending
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Monthly Disposable Income (auto-calculated) */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="coins" />
            </div>
            <Form.Group className="mb-3">
              <label>Disposable income available to save</label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={calculatedDisposable !== null ? calculatedDisposable : (contribution.monthlyDisposable ?? '')}
                    onChange={(e) => handleChange('monthlyDisposable', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('monthlyDisposable', 0); }}
                    min={0}
                    step={50}
                    style={calculatedDisposable !== null ? { backgroundColor: '#f0fff4', fontWeight: 600 } : {}}
                />
                <InputGroup.Text>/month</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                {calculatedDisposable !== null
                    ? <>Auto-calculated: £{netMonthlyIncome.toLocaleString()} take-home minus expenses. Override if needed.</>
                    : <>Enter your monthly take-home pay above to auto-calculate this.</>
                }
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Currently Saving Regularly */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="calendar-check" />
            </div>
            <Form.Group className="mb-3">
              <label>Are you currently saving regularly?</label>
              <Form.Select
                  value={contribution.savingRegularly || 'no'}
                  onChange={(e) => handleChange('savingRegularly', e.target.value)}
              >
                <option value="no">No — not currently saving</option>
                <option value="occasionally">Occasionally — when I can</option>
                <option value="yes_small">Yes — small amounts (under £200/month)</option>
                <option value="yes_moderate">Yes — moderate amounts (£200-£500/month)</option>
                <option value="yes_significant">Yes — significant amounts (over £500/month)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Outside of your pension contributions
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Other Pension Savings */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="piggy-bank" />
            </div>
            <Form.Group className="mb-3">
              <label>Do you have other pension pots or retirement savings?</label>
              <Form.Select
                  value={contribution.otherPensions}
                  onChange={(e) => handleChange('otherPensions', e.target.value)}
              >
                <option value="no">No — this is my only pension</option>
                <option value="small">Yes — small amounts from previous jobs</option>
                <option value="significant">Yes — significant savings elsewhere</option>
                <option value="defined_benefit">Yes — I have a defined benefit pension</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Col>

        {/* Summary Card */}
        <Col md={12}>
          <div className="profile-card bg-light">
            <h6 className="mb-3">
              <FontAwesomeIcon icon="calculator" className="me-2 text-primary" />
              Current Contribution Summary
            </h6>
            <Row>
              <Col sm={4}>
                <div className="text-center">
                  <div className="text-muted small">Your Contribution</div>
                  <div className="h5 mb-0">{contribution.employeeContribution}%</div>
                  <div className="text-muted small">£{monthlyEmployeeContribution.toLocaleString()}/mo</div>
                </div>
              </Col>
              <Col sm={4}>
                <div className="text-center">
                  <div className="text-muted small">Employer Contribution</div>
                  <div className="h5 mb-0">{contribution.employerContribution}%</div>
                  <div className="text-muted small">£{monthlyEmployerContribution.toLocaleString()}/mo</div>
                </div>
              </Col>
              <Col sm={4}>
                <div className="text-center">
                  <div className="text-muted small">Total Monthly</div>
                  <div className="h5 mb-0 text-success">£{totalMonthlyContribution.toLocaleString()}</div>
                  <div className="text-muted small">{contribution.employeeContribution + contribution.employerContribution}% of salary</div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>

        {/* Alert if missing employer match */}
        {missingEmployerMatch && (
            <Col md={12}>
              <Alert variant="warning" className="mt-3">
                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                <strong>You're missing out on free money!</strong> If you increase your contribution
                from {contribution.employeeContribution}% to {optimalEmployeeContrib}%, your employer
                would contribute {maxEmployerContrib}% instead of {contribution.employerContribution}%.
                That's an extra £{Math.round((salary * (maxEmployerContrib - contribution.employerContribution) / 100) / 12).toLocaleString()}/month!
              </Alert>
            </Col>
        )}
      </Row>
  );
};

export default ContributionDetails;